/**
 * useUcAdmin — данные и мутации EO-вкладки «Юникоины» (screen-22, остаток E4;
 * юникоины ПЕРСОНАЛА — не путать с клиентской экономикой Юнифанс). Загрузка —
 * allSettled четырёх скоупов с ok-гардами по шаблону useServicesAdmin:
 * балансы по периоду (GET /api/tg/balances?from&to), общий лог (GET
 * /api/uc/log — леджер + лента наклеек одной ручкой), история обменов
 * (GET /api/uc/exchanges) и, при активном фильтре, серверный леджер
 * оператора (GET /api/tg/ledger?operator_id — УД5). Провал скоупа держит
 * старые данные + errHint (loadErr), PTR перезагружает всё.
 *
 * УД2: пресеты периода считаются из РЕАЛЬНОЙ текущей даты идиомой Финансов
 * (todayBusiness/addDays/fmtDateApi, «Этот месяц» = 01.MM–сегодня как в
 * эталоне js:40). УД5: «Показать ещё» поверх сервера — растущий limit лога
 * (шаг 100, серверный клэмп 500 = глубина истории панели; offset не нужен).
 * Период-фильтр Обменов — клиентский по created_at (эталон js:204-206 —
 * дата ПРОВЕДЕНИЯ; серверный ?from фильтрует period_date — дату зарплаты).
 * Мутации (POST /api/tg/bonus, /api/uc/exchange) возвращают null | текст
 * ошибки для inline-ferr (УД9 — серверный отказ обмена тем же ferr);
 * успех = await перезагрузки + flash строки (эталон js:96,129).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { ApiException, type ApiList, type ApiOk } from "../api/types";
import type {
  UcBalanceRow, UcBonusBody, UcExchangeBody, UcExchangeRow,
  UcLedgerRow, UcLogResponse, UcLogSticker,
} from "../api/ucTypes";
import { addDays, fmtDateApi, todayBusiness } from "../utils/date";

const LOG_STEP = 100;
/** Серверный клэмп limit (uc-games.ts:699). */
const LOG_CAP = 500;
/** Серверный клэмп limit у /api/tg/ledger (unicoins.ts:73). */
const LEDGER_LIMIT = 200;

export interface UcPeriod { id: string; label: string; lab: string; from: string; to: string }
/** Пресет фильтра обменов (эталон XPER js:202): from=null — «Все». */
export interface UcXPreset { id: string; label: string; from: string | null }

/** УД2: пресеты из реальной текущей даты (идиома пресетов EoFinanceView). */
function buildPresets(): { presets: UcPeriod[]; xPresets: UcXPreset[]; def: UcPeriod } {
  const t = todayBusiness();
  const dd = (d: Date) =>
    `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  const mk = (id: string, label: string, from: Date, to: Date): UcPeriod => ({
    id, label, from: fmtDateApi(from), to: fmtDateApi(to),
    lab: from.getTime() === to.getTime() ? dd(from) : `${dd(from)}–${dd(to)}`,
  });
  const y = t.getUTCFullYear();
  const m = t.getUTCMonth();
  const d7 = mk("d7", "7 дней", addDays(t, -6), t);
  const m0 = mk("m0", "Этот месяц", new Date(Date.UTC(y, m, 1)), t);
  return {
    presets: [
      mk("d1", "Сегодня", t, t),
      d7,
      m0,
      mk("m1", "Прошлый месяц", new Date(Date.UTC(y, m - 1, 1)), new Date(Date.UTC(y, m, 0))),
    ],
    xPresets: [
      { id: "all", label: "Все", from: null },
      { id: "d7", label: "7 дней", from: d7.from },
      { id: "m0", label: "Этот месяц", from: m0.from },
    ],
    /* Дефолт «7 дней» — эталон per=PRESETS[1] (js:41). */
    def: d7,
  };
}

interface UcData {
  bal: UcBalanceRow[]; led: UcLedgerRow[]; st: UcLogSticker[]; x: UcExchangeRow[];
  opLed: UcLedgerRow[]; loaded: boolean;
  ok: { bal: boolean; log: boolean; x: boolean; oled: boolean } | null;
}
const D0: UcData = { bal: [], led: [], st: [], x: [], opLed: [], loaded: false, ok: null };

export interface UcAdmin {
  presets: UcPeriod[]; xPresets: UcXPreset[];
  period: UcPeriod; setPeriod: (p: UcPeriod) => void;
  balances: UcBalanceRow[];
  /** Активный источник леджера: серверный фильтр оператора либо общий лог. */
  ledger: UcLedgerRow[];
  stickers: UcLogSticker[];
  exchanges: UcExchangeRow[];
  ledOp: string | null; setLedOp: (id: string | null) => void;
  /** Общий лог мог не кончиться на сервере (лимит вырос не до клэмпа). */
  hasMoreLog: boolean; moreLog: () => void;
  xPer: UcXPreset; setXPer: (p: UcXPreset) => void;
  xOp: string | null; setXOp: (id: string | null) => void;
  loaded: boolean; loadErr: boolean; busy: boolean;
  /** flashOk первой строки после мутации: key 'led' | 'x' + счётчик. */
  flash: { key: string; n: number } | null;
  /** null = успех; строка = текст для inline-ferr (УД9). */
  bonus: (b: UcBonusBody) => Promise<string | null>;
  exchange: (b: UcExchangeBody) => Promise<string | null>;
  nameOf: (id: string | null) => string | undefined;
  refetch: () => Promise<void>;
}

export function useUcAdmin(): UcAdmin {
  const { presets, xPresets, def } = useMemo(buildPresets, []);
  const [period, setPeriod] = useState<UcPeriod>(def);
  const [xPer, setXPer] = useState<UcXPreset>({ id: "all", label: "Все", from: null });
  const [xOp, setXOp] = useState<string | null>(null);
  const [ledOp, setLedOp] = useState<string | null>(null);
  const [logN, setLogN] = useState(LOG_STEP);
  const [data, setData] = useState<UcData>(D0);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const flashT = useRef(0);
  /* Поколение загрузки: гасит гонку перезагрузок (идиома dead-флага
     useServicesAdmin) — опоздавший allSettled-батч (GET-ретраи с бэкоффом
     до секунд) не должен перетереть данные более нового запуска. */
  const genRef = useRef(0);

  const load = useCallback(async () => {
    const gen = ++genRef.current;
    const xs = xOp ? `operator_id=${encodeURIComponent(xOp)}` : "";
    const [rb, rl, rx, ro] = await Promise.allSettled([
      api<ApiList<UcBalanceRow>>(`/api/tg/balances?from=${period.from}&to=${period.to}`),
      api<UcLogResponse>(`/api/uc/log?limit=${logN}`),
      api<ApiList<UcExchangeRow>>(`/api/uc/exchanges${xs ? `?${xs}` : ""}`),
      ledOp
        ? api<ApiList<UcLedgerRow>>(`/api/tg/ledger?operator_id=${encodeURIComponent(ledOp)}&limit=${LEDGER_LIMIT}`)
        : Promise.resolve(null),
    ]);
    if (gen !== genRef.current) return; // устаревший запуск — не применять
    const bal = rb.status === "fulfilled" ? rb.value.results ?? [] : null;
    const log = rl.status === "fulfilled" ? rl.value : null;
    const x = rx.status === "fulfilled" ? rx.value.results ?? [] : null;
    const oled = ro.status === "fulfilled" ? (ro.value ? ro.value.results ?? [] : []) : null;
    setData((old) => ({
      bal: bal ?? old.bal,
      led: log ? log.ledger : old.led,
      st: log ? log.stickers : old.st,
      x: x ?? old.x,
      opLed: oled ?? old.opLed,
      loaded: old.loaded || !!(bal || log || x),
      ok: { bal: !!bal, log: !!log, x: !!x, oled: !!oled },
    }));
  }, [period.from, period.to, logN, xOp, ledOp]);

  useEffect(() => { void load(); }, [load]);
  /* Unmount: погасить flash-таймер и осиротевшие setData летящих загрузок. */
  useEffect(() => () => { genRef.current += 1; window.clearTimeout(flashT.current); }, []);

  const doFlash = (key: string) => {
    setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }));
    window.clearTimeout(flashT.current);
    flashT.current = window.setTimeout(() => setFlash(null), 900);
  };

  /** Успех = перезагрузка ДО flash — вспыхивает уже свежая первая строка. */
  const mutate = async (fn: () => Promise<unknown>, key: string): Promise<string | null> => {
    setBusy(true);
    try {
      await fn();
      await load();
      doFlash(key);
      return null;
    } catch (err) {
      return err instanceof ApiException
        ? err.body?.error ?? `Ошибка ${err.status}`
        : "Ошибка сети — попробуйте ещё раз";
    } finally {
      setBusy(false);
    }
  };

  const names = useMemo(() => {
    const m: Record<string, string> = {};
    for (const b of data.bal) m[b.operator_id] = b.operator_name;
    return m;
  }, [data.bal]);

  /* Период Обменов — по дате ПРОВЕДЕНИЯ (created_at, эталон js:204-206);
     серверного фильтра по created_at нет (?from — это period_date, дата
     зарплаты) → дофильтр клиентски (УД5). Строковое сравнение корректно:
     created_at 'YYYY-MM-DD HH:MM:SS' >= from 'YYYY-MM-DD' включает from-день. */
  const exchanges = useMemo(() => {
    const f = xPer.from;
    return f ? data.x.filter((r) => r.created_at >= f) : data.x;
  }, [data.x, xPer.from]);

  return {
    presets, xPresets,
    period, setPeriod,
    balances: data.bal,
    ledger: ledOp ? data.opLed : data.led,
    stickers: data.st,
    exchanges,
    ledOp, setLedOp,
    hasMoreLog: !ledOp && data.led.length >= logN && logN < LOG_CAP,
    moreLog: () => setLogN((n) => Math.min(LOG_CAP, n + LOG_STEP)),
    xPer, setXPer, xOp, setXOp,
    loaded: data.loaded,
    loadErr: data.ok != null && !(data.ok.bal && data.ok.log && data.ok.x && data.ok.oled),
    busy, flash,
    /* retries:0 — конвенция денежных POST (useDictCard.ts:80-82): сервер
       неидемпотентен, авто-повтор при потерянном ответе задваивал бы
       бонус/списание; сетевая ошибка и так уходит формам как inline-ferr. */
    bonus: (b) => mutate(() => api<ApiOk>("/api/tg/bonus", { method: "POST", body: b, retries: 0 }), "led"),
    exchange: (b) => mutate(() => api<ApiOk>("/api/uc/exchange", { method: "POST", body: b, retries: 0 }), "x"),
    nameOf: (id) => (id ? names[id] : undefined),
    refetch: load,
  };
}
